/**
 * WhatsAppTemplateEditorPage
 *
 * Two routes use this page:
 *   /whatsapp/templates/new   → blank editor
 *   /whatsapp/templates/:id   → load + edit existing template
 *
 * Left column hosts the TemplateBuilder form; the right column shows
 * a live WhatsAppPreview with placeholder sample values so the marketer
 * sees the result without having to send.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Send,
  Loader2,
  AlertCircle,
  Sparkles,
  Eye,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { whatsappApi } from "../api";
import { TemplateBuilder, type TemplateDraft } from "../components/TemplateBuilder";
import { WhatsAppPreview } from "../components/WhatsAppPreview";
import { AITemplateGenerator } from "../components/AITemplateGenerator";
import type { WhatsAppTemplate } from "../types";

const MOCK_BRAND_ID = "brand-demo";

const BLANK_DRAFT: TemplateDraft = {
  name: "",
  category: "marketing",
  language: "es_AR",
  header: { type: "none" },
  body: "",
  footer: "",
  buttons: [],
};

export function WhatsAppTemplateEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [draft, setDraft] = useState<TemplateDraft>(BLANK_DRAFT);
  const [, setLoadedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  /* ─── Load existing template when editing ─── */
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      const res = await whatsappApi.getTemplateById(id!);
      if (res.success) {
        const t = res.data;
        setLoadedTemplate(t);
        setDraft({
          name: t.name,
          category: t.category,
          language: t.language,
          header: t.header || { type: "none" },
          body: t.body,
          footer: t.footer || "",
          buttons: t.buttons || [],
        });
      } else {
        setError(res.error || "No se pudo cargar la plantilla");
      }
      setLoading(false);
    })();
  }, [id, isNew]);

  /* ─── Validation ─── */
  const validationError = useMemo(() => {
    if (!draft.name.trim()) return "El nombre es requerido";
    if (!draft.body.trim()) return "El cuerpo del mensaje es requerido";
    if (draft.header.type === "text" && !draft.header.text?.trim())
      return "El encabezado de texto no puede estar vacío";
    // Validate buttons
    for (const btn of draft.buttons) {
      if (!btn.label.trim()) return "Todos los botones requieren un texto";
      if (btn.type === "url" && !btn.url?.trim())
        return "Los botones de URL requieren un link";
      if (btn.type === "phone_number" && !btn.phoneNumber?.trim())
        return "Los botones de llamada requieren un teléfono";
    }
    return null;
  }, [draft]);

  // Sin estado de aprobación Meta: todos los mensajes guardados son editables.
  const canEdit = true;

  /* ─── Save ─── */
  const handleSave = async (
    _options: { submitAfter?: boolean } = {},
  ) => {
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSuccess(null);
    setSaving(true);

    const payload = {
      name: draft.name.trim(),
      category: draft.category,
      language: draft.language,
      header:
        draft.header.type === "none" ? { type: "none" as const } : draft.header,
      body: draft.body.trim(),
      footer: draft.footer.trim() || undefined,
      buttons: draft.buttons.length ? draft.buttons : undefined,
    };

    try {
      let savedId: string;

      if (isNew) {
        const res = await whatsappApi.createTemplate({
          brandId: MOCK_BRAND_ID,
          ...payload,
        });
        if (!res.success) {
          setError(res.error || "Error al guardar");
          setSaving(false);
          return;
        }
        savedId = res.data.id;
        setSuccess("Plantilla creada");
      } else {
        const res = await whatsappApi.updateTemplate(id!, payload);
        if (!res.success) {
          setError(res.error || "Error al guardar");
          setSaving(false);
          return;
        }
        savedId = id!;
        setSuccess("Plantilla actualizada");
      }

      // (Sin submit a Meta — los mensajes guardados son de uso libre)
      void savedId;

      // Navigate back to list shortly after success
      setTimeout(() => navigate("/whatsapp/templates"), 1200);
    } catch (e: any) {
      setError(e.message || "Error inesperado");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Preview template (synthetic) ─── */
  const previewTemplate = useMemo(
    () =>
      ({
        id: id || "draft",
        name: draft.name || "borrador",
        category: draft.category,
        language: draft.language,
        approvalStatus: "approved" as const,
        header: draft.header,
        body: draft.body || "Tu mensaje aparecerá acá…",
        variableCount: (draft.body.match(/\{\{(\d+)\}\}/g) || []).length,
        footer: draft.footer || undefined,
        buttons: draft.buttons.length ? draft.buttons : undefined,
        estimatedCostUSD: 0.0573,
      }) satisfies WhatsAppTemplate,
    [id, draft],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando plantilla...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Breadcrumb */}
        <Link
          to="/whatsapp/templates"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600 transition-colors"
        >
          <ArrowLeft size={14} />
          Volver a mensajes guardados
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {isNew ? "Nuevo mensaje guardado" : "Editar mensaje guardado"}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {isNew
                ? "Plantilla reusable con variables. Después la usás desde cualquier cuenta vinculada."
                : "Edita el contenido. Los cambios aplican a futuras campañas — las ya enviadas no se modifican."}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowAIGenerator(true)}
              disabled={saving}
            >
              <Sparkles size={15} />
              Sugerir con IA
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSave()}
              disabled={saving || !!validationError}
            >
              {saving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Save size={15} />
              )}
              Guardar mensaje
            </Button>
          </div>
        </div>

        {/* Error / Success */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 animate-fade-in">
            <Send size={18} />
            <span className="text-sm font-medium">{success}</span>
          </div>
        )}

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: builder */}
          <div className="lg:col-span-3">
            <fieldset disabled={!canEdit} className="space-y-6">
              <TemplateBuilder
                draft={draft}
                onChange={setDraft}
                nameLocked={!isNew}
                onAISuggest={() => setShowAIGenerator(true)}
              />
            </fieldset>
          </div>

          {/* Right: preview */}
          <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-6 self-start">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-2">
              <Eye size={14} className="text-slate-400" />
              <p className="text-xs text-slate-500 leading-tight">
                Vista previa en vivo — los <code className="font-mono text-emerald-600">{"{{N}}"}</code>{" "}
                se muestran como placeholders hasta que envíes la campaña.
              </p>
            </div>

            <WhatsAppPreview
              template={previewTemplate}
              bodyVariables={{}}
              headerVariable={
                draft.header.type === "text" ? "Juan" : undefined
              }
            />
          </div>
        </div>
      </div>

      <AITemplateGenerator
        open={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        onAccept={(tpl) => {
          setDraft({
            name: tpl.name,
            category: tpl.category,
            language: tpl.language,
            header: tpl.header || { type: "none" },
            body: tpl.body,
            footer: tpl.footer || "",
            buttons: tpl.buttons || [],
          });
          setError(null);
        }}
      />
    </div>
  );
}

export default WhatsAppTemplateEditorPage;
